import { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutAnimation } from 'react-native';

import {
  Group,
  SearchableResult,
  User,
  useListUsersLazyQuery,
  useSearchLazyQuery,
} from '@graphql/generated';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { Search } from '@root/types';
import { isContactSearchResult, isGroupSearchResult } from '@utils/typeGuards';

interface UseMemberSearchOptions {
  contacts?: User[];
  meId?: string;
  includeContacts?: boolean;
  useLayoutAnimation?: boolean;
}

/**
 * Shared member search infrastructure for invite/assign flows.
 * Manages search state, debounced global search, lazy user queries,
 * and group expansion toggles.
 *
 * Used by TaskInviteOrAssignee, ProjectInviteOrAddMember, and InviteMemberModal.
 */
export const useMemberSearch = ({
  includeContacts = true,
  useLayoutAnimation: useLayoutAnim = false,
}: UseMemberSearchOptions = {}) => {
  const [searchVal, setText] = useState('');
  const [focusOnSearch, setFocusOnSearch] = useState(false);

  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
  const [searchExpandedGroupIds, setSearchExpandedGroupIds] = useState<
    string[]
  >([]);

  const [listUsers, { data: taskTagUsers, loading: listUsersLoading }] =
    useListUsersLazyQuery();
  const [globalSearch, { data: searchRtnData, loading: globalSearchLoading }] =
    useSearchLazyQuery();

  const runGlobalSearch = useCallback(
    (term: string) => {
      globalSearch({
        variables: {
          term,
          size: 20,
          includeChats: false,
          includeMessages: false,
          includeProjects: false,
          includeDocuments: false,
          includeTasks: false,
          includeContacts,
          includeCompletedTask: false,
          includeTeams: false,
          includeGroups: true,
          includeUsers: false,
        },
      });
    },
    [globalSearch, includeContacts]
  );

  const executeSearch = useCallback(
    (term: string) => {
      runGlobalSearch(term);
      listUsers({ variables: { term, size: 20 } });
    },
    [runGlobalSearch, listUsers]
  );

  const { isPending, hasSearched } = useDebouncedSearch(
    searchVal,
    executeSearch
  );
  // Debounce-pending OR either lazy query still in flight — callers gate
  // their search-results skeleton on this instead of re-deriving it from
  // the two Apollo hooks themselves (TA-10280).
  const isSearchLoading = isPending || listUsersLoading || globalSearchLoading;

  useEffect(() => {
    setSearchExpandedGroupIds([]);
  }, [searchVal]);

  const searchData = useMemo(
    () => (searchRtnData?.search as Search<SearchableResult>[]) || [],
    [searchRtnData]
  );

  const listContacts: User[] = useMemo(() => {
    // hasSearched is false below the min-char threshold and while a newer
    // term is still debouncing, so gating on it here stops Apollo's cached
    // last-response data from leaking in as if it were live results for the
    // current (shorter/cleared) term.
    if (!hasSearched) return [];
    return searchData
      .filter((t) => isContactSearchResult(t))
      .map(
        (t) =>
          ({
            ...t.record,
            name: (t.record as any).fullName ?? t.record.name,
            fullName: null,
          }) as User
      );
  }, [searchData, hasSearched]);

  const listGroup: Group[] = useMemo(() => {
    if (!hasSearched) return [];
    return searchData
      .filter((t) => isGroupSearchResult(t))
      .map((t) => t.record as Group);
  }, [searchData, hasSearched]);

  const listTasktagUsers: User[] = useMemo(() => {
    if (!hasSearched) return [];
    return (taskTagUsers?.listUsers ?? []).filter((u): u is User => u != null);
  }, [taskTagUsers, hasSearched]);

  const toggleGroupExpanded = useCallback(
    (groupId: string) => {
      if (useLayoutAnim) {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
      setExpandedGroupIds((prev) =>
        prev.includes(groupId)
          ? prev.filter((id) => id !== groupId)
          : [...prev, groupId]
      );
    },
    [useLayoutAnim]
  );

  const toggleSearchGroupExpanded = useCallback(
    (groupId: string) => {
      if (useLayoutAnim) {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
      setSearchExpandedGroupIds((prev) =>
        prev.includes(groupId)
          ? prev.filter((id) => id !== groupId)
          : [...prev, groupId]
      );
    },
    [useLayoutAnim]
  );

  const resetExpanded = useCallback(() => {
    setExpandedGroupIds([]);
    setSearchExpandedGroupIds([]);
  }, []);

  const shouldShowDropdown = focusOnSearch && searchVal.trim().length > 0;

  return {
    searchVal,
    setText,
    focusOnSearch,
    setFocusOnSearch,
    shouldShowDropdown,
    listContacts,
    listGroup,
    listTasktagUsers,
    expandedGroupIds,
    searchExpandedGroupIds,
    toggleGroupExpanded,
    toggleSearchGroupExpanded,
    resetExpanded,
    // Exposed so consumers can fall back to their own local/unfiltered data
    // below the min-char threshold instead of flashing an empty search
    // section (TA-10166) — see useDebouncedSearch's doc comment for
    // the exact semantics.
    hasSearched,
    isSearchLoading,
  };
};
